import { Controller } from "@hotwired/stimulus"

// The confirmation step on the school-facing "New Design" page: "Request this
// design" doesn't post, it shows what the school chose -- right under the
// school name -- with Submit, Edit and Cancel.
//
// It lives in the browser rather than as a server round trip on purpose: a file
// input cannot be re-filled after a page load, so a server-rendered review step
// would silently drop the logo the school just chose. Here the form never
// leaves the page, so Edit hands every answer back untouched.
//
// The summary is built from whatever fields are tagged as `field` targets, each
// carrying its own label, so adding a question to the form means tagging it --
// not teaching this controller about it.
export default class extends Controller {
  static targets = [
    "form", "picker", "intro", "confirmation", "summary",
    "awardTitle", "awardImage", "confirmTitle", "confirmImage", "field", "submitButton", "reviewButton"
  ]

  review() {
    this.renderSummary()

    this.pickerTarget.hidden = true
    if (this.hasIntroTarget) this.introTarget.hidden = true
    this.confirmationTarget.hidden = false
    this.confirmationTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Back to the form with every answer as the school left it.
  edit() {
    this.close()
    // Back to the bottom of the options, where the school clicked to review.
    this.reviewButtonTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // Abandon this request: clear the answers back to their defaults and drop the
  // chosen award. The award picker owns the selection, so it is told rather
  // than reached into (award-catalog#reset listens for this event).
  cancel() {
    this.formTarget.reset()
    this.close()
    this.dispatch("cancelled")
    this.element.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // One click, one request -- a second click while the post is in flight would
  // file the design twice.
  submitting() {
    this.submitButtonTarget.disabled = true
  }

  close() {
    this.confirmationTarget.hidden = true
    this.pickerTarget.hidden = false
    if (this.hasIntroTarget) this.introTarget.hidden = false
  }

  renderSummary() {
    this.confirmTitleTarget.textContent = this.awardTitleTarget.textContent.trim()

    const src = this.awardImageTarget.getAttribute("src")
    this.confirmImageTarget.hidden = !src
    if (src) {
      this.confirmImageTarget.src = src
      this.confirmImageTarget.alt = this.confirmTitleTarget.textContent
    } else {
      this.confirmImageTarget.removeAttribute("src")
    }

    // textContent, never innerHTML: notes are free text typed by the school.
    this.summaryTarget.replaceChildren(...this.fieldTargets.flatMap((field) => {
      const term = document.createElement("dt")
      term.textContent = field.dataset.confirmLabel

      const detail = document.createElement("dd")
      detail.textContent = this.answer(field)

      return [term, detail]
    }))
  }

  answer(field) {
    if (field.tagName === "SELECT") {
      const option = field.selectedOptions[0]
      return option && option.value !== "" ? option.text : (field.dataset.confirmEmpty || "None")
    }

    if (field.type === "file") {
      return field.files && field.files.length ? field.files[0].name : (field.dataset.confirmEmpty || "None")
    }

    return field.value.trim() || field.dataset.confirmEmpty || "None"
  }
}
